import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, SectionTitle } from "@/components/common/Page";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Save, Sparkles, ArrowRight, ArrowLeft, Check } from "lucide-react";
import { SecurityPanel } from "@/components/settings/SecurityPanel";

const inputCls = "border-[#2D3F62] bg-[#0B1120] text-slate-100 placeholder:text-slate-600 focus-visible:ring-cyan-500";

const groups = [
  { title: "Company", fields: [
    { k: "legal_name", l: "Legal Company Name" },
    { k: "trading_name", l: "Trading Name" },
    { k: "abn", l: "ABN" },
    { k: "acn", l: "ACN" },
    { k: "contact_email", l: "Contact Email" },
    { k: "contact_phone", l: "Contact Phone" },
    { k: "address", l: "Address" },
  ]},
  { title: "Financial", fields: [
    { k: "currency", l: "Currency" },
    { k: "timezone", l: "Timezone" },
    { k: "financial_year_start", l: "Financial Year Start (MM-DD)" },
    { k: "opening_cash", l: "Opening Cash", t: "number" },
    { k: "invoice_terms_days", l: "Invoice Terms (days)", t: "number" },
    { k: "gst_registered", l: "GST Registered", t: "switch" },
  ]},
  { title: "Labour & Targets", fields: [
    { k: "founder_internal_hourly_cost", l: "Founder Internal Hourly Cost", t: "number" },
    { k: "founder_commercial_hourly_rate", l: "Founder Commercial Hourly Rate", t: "number" },
    { k: "target_gross_margin", l: "Target Gross Margin %", t: "number" },
    { k: "target_customer_margin", l: "Target Customer Margin %", t: "number" },
    { k: "budget_alert_threshold", l: "Budget Alert Threshold %", t: "number" },
    { k: "cash_runway_alert_months", l: "Cash Runway Alert (months)", t: "number" },
  ]},
  { title: "Integrations", fields: [
    { k: "integration_base_url", l: "Production Integration API URL" },
  ]},
];

export default function Settings() {
  const qc = useQueryClient();
  const [form, setForm] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });

  useEffect(() => {
    if (data) {
      setForm(data);
      if (!data.setup_complete) setWizardOpen(true);
    }
  }, [data]);

  const save = useMutation({
    mutationFn: async (payload) => (await api.put("/settings", payload)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["settings"] }); qc.invalidateQueries({ queryKey: ["overview"] }); toast.success("Settings saved"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  if (isLoading || !form) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-cyan-400" /></div>;

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const saveGroup = () => {
    const payload = { ...form };
    ["founder_internal_hourly_cost", "founder_commercial_hourly_rate", "target_gross_margin", "target_customer_margin", "budget_alert_threshold", "cash_runway_alert_months", "opening_cash", "invoice_terms_days"].forEach((k) => { payload[k] = Number(payload[k]) || 0; });
    save.mutate(payload);
  };

  return (
    <div className="ns-fade">
      <PageHeader title="Company Settings" subtitle="NavScora business configuration" testId="settings-header">
        <Button variant="outline" className="border-cyan-500/30 bg-transparent text-cyan-300 hover:bg-cyan-500/10" onClick={() => setWizardOpen(true)} data-testid="open-setup-wizard">
          <Sparkles className="mr-1.5 h-4 w-4" /> Setup Wizard
        </Button>
        <Button className="bg-gradient-to-r from-cyan-500 to-sky-600 font-semibold text-slate-900 hover:from-cyan-400 hover:to-sky-500" onClick={saveGroup} disabled={save.isPending} data-testid="save-settings-button">
          <Save className="mr-1.5 h-4 w-4" /> Save Changes
        </Button>
      </PageHeader>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {groups.map((g) => (
          <div key={g.title} className="rounded-xl border border-[#1E2D4A] bg-[#111A2E]/70 p-5">
            <SectionTitle className="mb-4">{g.title}</SectionTitle>
            <div className="space-y-3">
              {g.fields.map((f) => (
                <div key={f.k}>
                  {f.t === "switch" ? (
                    <div className="flex items-center justify-between rounded-lg border border-[#2D3F62] bg-[#0B1120] px-3 py-2.5">
                      <span className="text-sm text-slate-300">{f.l}</span>
                      <Switch checked={!!form[f.k]} onCheckedChange={(v) => set(f.k, v)} data-testid={`setting-${f.k}`} />
                    </div>
                  ) : (
                    <>
                      <Label className="text-xs text-slate-400">{f.l}</Label>
                      <Input type={f.t || "text"} value={form[f.k] ?? ""} onChange={(e) => set(f.k, e.target.value)} className={`mt-1 ${inputCls}`} data-testid={`setting-${f.k}`} />
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <SecurityPanel />

      <SetupWizard open={wizardOpen} onOpenChange={setWizardOpen} settings={form} onComplete={(payload) => { save.mutate({ ...payload, setup_complete: true }); setWizardOpen(false); }} />
    </div>
  );
}

const WIZARD_STEPS = [
  { title: "Company", fields: [{ k: "legal_name", l: "Legal Company Name" }, { k: "trading_name", l: "Trading Name" }, { k: "abn", l: "ABN" }] },
  { title: "GST & Financial Year", fields: [{ k: "gst_registered", l: "GST Registered", t: "switch" }, { k: "financial_year_start", l: "FY Start (MM-DD)" }, { k: "currency", l: "Currency" }] },
  { title: "Opening Cash", fields: [{ k: "opening_cash", l: "Opening Cash Balance", t: "number" }] },
  { title: "Founder Labour Rate", fields: [{ k: "founder_internal_hourly_cost", l: "Internal Hourly Cost", t: "number" }, { k: "founder_commercial_hourly_rate", l: "Commercial Hourly Rate", t: "number" }] },
  { title: "Margin Targets", fields: [{ k: "target_gross_margin", l: "Target Gross Margin %", t: "number" }, { k: "target_customer_margin", l: "Target Customer Margin %", t: "number" }] },
  { title: "Alerts", fields: [{ k: "budget_alert_threshold", l: "Budget Alert Threshold %", t: "number" }, { k: "cash_runway_alert_months", l: "Cash Runway Alert (months)", t: "number" }] },
];

function SetupWizard({ open, onOpenChange, settings, onComplete }) {
  const [step, setStep] = useState(0);
  const [vals, setVals] = useState({});
  useEffect(() => { if (open) { setVals(settings || {}); setStep(0); } }, [open]);
  const s = WIZARD_STEPS[step];
  const set = (k, v) => setVals((x) => ({ ...x, [k]: v }));
  const last = step === WIZARD_STEPS.length - 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-[#1E2D4A] bg-[#111A2E] text-slate-100 sm:max-w-lg" data-testid="setup-wizard">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-cyan-400" /> Initial Setup — Step {step + 1} of {WIZARD_STEPS.length}</DialogTitle>
        </DialogHeader>
        <div className="mb-2 flex gap-1">
          {WIZARD_STEPS.map((_, i) => (
            <div key={i} className={`h-1 flex-1 rounded-full ${i <= step ? "bg-cyan-400" : "bg-[#2D3F62]"}`} />
          ))}
        </div>
        <div className="py-2">
          <h3 className="mb-4 text-lg font-semibold text-slate-100">{s.title}</h3>
          <div className="space-y-3">
            {s.fields.map((f) => (
              <div key={f.k}>
                {f.t === "switch" ? (
                  <div className="flex items-center justify-between rounded-lg border border-[#2D3F62] bg-[#0B1120] px-3 py-2.5">
                    <span className="text-sm text-slate-300">{f.l}</span>
                    <Switch checked={!!vals[f.k]} onCheckedChange={(v) => set(f.k, v)} data-testid={`wizard-${f.k}`} />
                  </div>
                ) : (
                  <>
                    <Label className="text-xs text-slate-400">{f.l}</Label>
                    <Input type={f.t || "text"} value={vals[f.k] ?? ""} onChange={(e) => set(f.k, e.target.value)} className={`mt-1 ${inputCls}`} data-testid={`wizard-${f.k}`} />
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
        <DialogFooter className="flex-row justify-between sm:justify-between">
          <Button variant="ghost" className="text-slate-400" disabled={step === 0} onClick={() => setStep((x) => x - 1)} data-testid="wizard-back">
            <ArrowLeft className="mr-1 h-4 w-4" /> Back
          </Button>
          {last ? (
            <Button className="bg-gradient-to-r from-emerald-500 to-cyan-500 font-semibold text-slate-900" onClick={() => onComplete(vals)} data-testid="wizard-complete">
              <Check className="mr-1 h-4 w-4" /> Complete Setup
            </Button>
          ) : (
            <Button className="bg-gradient-to-r from-cyan-500 to-sky-600 font-semibold text-slate-900" onClick={() => setStep((x) => x + 1)} data-testid="wizard-next">
              Next <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
